import { DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { capitalize } from "@mui/material";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import TrashIcon from "../../../Assets/svgs/TrashIcon";
import {
  ALL_CALLING_CODES,
  ALL_FORM_ACTIONS,
  INITIAL_FILE_TYPES,
} from "../../../Common/Constants";
import DateSelector from "../../../Common/DateSelector";
import MultiSelectDropdown from "../../../Common/MultiSelectDropdown";
import PlusIconBtn from "../../../Common/PlusIconBtn";
import { uuidGenerator } from "../../../Common/utils";
import { nameWithNumberValidation } from "../../../Common/Validations";
import AddFormFieldsModal from "../../../Model/AddFormFieldsModal";
import AddVariableModal from "../../../Model/AddVariableModal";
import { checkFormField } from "../../../Redux/Slice/Assistant/CheckFormFieldSlice";
import ReactQuillEditor from "../../../Common/ReactQuillEditor";

const FormTab = ({
  isTabSelected,
  formData,
  setFormData,
  formError,
  setFormError,
  setIsLoading,
  showErrorMessage,
  assistantId,
}) => {
  const allFieldsData = Array.isArray(formData?.form?.fields)
    ? formData?.form?.fields
    : [];
  const formActionData = Array.isArray(formData?.form?.formActions)
    ? formData?.form?.formActions
    : [];
  const isData = Array.isArray(allFieldsData)
    ? allFieldsData?.length > 0
    : false;

  const dispatch = useDispatch();
  const [formFieldData, setFormFieldData] = useState({
    label: "",
    type: "",
    isDefault: false,
    required: true,
    visible: true,
    isFromAdd: true,
  });

  const [addVariableModalVisible, setAddVariableModalVisible] = useState({
    variableFor: null,
    visible: false,
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10, // Enable sort function when dragging 10px   💡 here!!!
    },
  });
  const sensors = useSensors(mouseSensor);
  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormData((prev) => {
        const oldIndex = prev.form?.fields.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = prev.form?.fields.findIndex(
          (item) => item.id === over.id
        );

        return {
          ...prev,
          form: {
            ...prev.form,
            fields: arrayMove(prev.form?.fields, oldIndex, newIndex),
          },
        };
      });
    }
  }

  const notDefaultFields = useMemo(
    () => allFieldsData?.filter((field) => !field?.isDefault),
    [allFieldsData]
  );

  const currentDefaultFields = useMemo(
    () => allFieldsData?.filter((field) => field?.isDefault),
    [allFieldsData]
  );

  const validateLabel = (label) => {
    const isValid = nameWithNumberValidation(label);
    if (isValid || label.trim() === "") {
      return "";
    } else {
      return "Name can only contain letters, numbers, spaces, hyphens, and apostrophes";
    }
  };

  const validateSize = (size) => {
    if (!size || size < 1 || size > 10)
      return "Size must be between 1 and 10 MB.";
    return "";
  };

  const [isShowAddFormModal, setIsShowAddFormModal] = useState(false);

  const onAddFieldData = (fieldData) => {
    setFormData((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        fields: [
          ...currentDefaultFields,
          ...fieldData?.map((field) => ({
            ...field,
            name: field?.label,
            id: field?.id ? field?.id : uuidGenerator(),
          })),
        ],
      },
    }));
    setFormFieldData({
      label: "",
      type: "",
      isDefault: false,
      required: true,
      visible: true,
      isFromAdd: true,
    });
  };

  const handleChange = (e) => {
    let { name, value, checked, type } = e?.target;
    value = name == "label" ? capitalize(value?.trimStart()) : value;
    setFormFieldData((prev) => ({
      ...prev,
      size:
        name == "type"
          ? value == "upload"
            ? 2
            : undefined
          : formFieldData?.size,
      extensionAllowed:
        name == "type"
          ? value == "upload"
            ? undefined
            : undefined
          : formFieldData?.extensionAllowed,
      [name]:
        type == "checkbox"
          ? checked
          : name == "label"
          ? capitalize(value)
          : value,
    }));
    setFormError((error) => {
      if (name === "label") {
        return { ...error, formLabelError: validateLabel(value) };
      }
      if (name === "size") {
        return { ...error, formSizeError: validateSize(Number(value)) };
      }
      return error;
    });
  };

  const showAddForm = () => {
    setIsShowAddFormModal(true);
  };

  const closeAddForm = () => {
    setIsShowAddFormModal(false);
  };

  const onRemoveField = async (index, field) => {
    let isValid = false;
    if (!field?.id) {
      isValid = true;
    } else {
      isValid = await checkFormFieldById(field?.id);
    }
    if (isValid) {
      setFormData((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          fields: prev?.form?.fields.filter(
            (_, fieldIndex) => fieldIndex != index
          ),
        },
      }));
    } else {
      showErrorMessage("Field can not be deleted, it's already used.");
    }
  };

  const checkFormFieldById = async (fieldId) => {
    try {
      setIsLoading(true);
      const result = await dispatch(checkFormField(fieldId));
      if (
        checkFormField.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        return true;
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormActionDataData = (selectedList) => {
    let selectedListIds = Array.isArray(selectedList)
      ? selectedList.map((item) => item?.value)
      : [];
    if (
      selectedListIds?.includes("email") &&
      !selectedListIds?.includes("createContact")
    ) {
      selectedListIds?.push("createContact");
    }
    setFormData((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        formActions: selectedListIds,
      },
    }));
  };

  const onSubmitAddVariable = (variable) => {
    setFormData((p) => ({
      ...p,
      form: {
        ...p.form,
        [addVariableModalVisible.variableFor]:
          typeof p.form[addVariableModalVisible.variableFor] == "string"
            ? p.form[addVariableModalVisible.variableFor] + variable
            : p.form[addVariableModalVisible.variableFor],
      },
    }));
    setAddVariableModalVisible({
      visible: false,
      variableFor: null,
    });
  };

  const FormFieldItem = ({ field, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: field?.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {field?.type == "input" ||
        field?.type == "text" ||
        field?.type == "number" ||
        field?.type == "url" ? (
          <div key={`${index}`} className="formCol">
            <div className="form__Field">
              <label for="input-field" className="form__label">
                {field?.name ?? field?.label}
                {field?.required ? (
                  <span style={{ color: "red" }}> * </span>
                ) : null}
              </label>
              {/* <input
        type="text"
        id="input-field"
        className="form__input form__input--sm"
        disabled
        placeholder={field?.label}
      /> */}
              <div className="form__groupField">
                <input
                  type="text"
                  id="input-field"
                  className="form__input form__input--sm"
                  disabled
                  placeholder={field?.label}
                />
                {field?.isDefault ? null : (
                  <button
                    className="btn"
                    onClick={() => onRemoveField(index, field)}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            </div>
            {index == allFieldsData?.length - 1 ? (
              <p className="para para--right">
                <button className="link link__primary" onClick={showAddForm}>
                  +{notDefaultFields?.length == 5 ? "Edit" : "Add More/Edit"}{" "}
                  Fields
                </button>
              </p>
            ) : null}
          </div>
        ) : field?.type == "upload" ? (
          <div key={`${index}`} className="formCol">
            <div className="form__Field">
              <label for="uploadFile" className="form__label">
                {field?.name ?? field?.label}
                {field?.required ? (
                  <span style={{ color: "red" }}> * </span>
                ) : null}
              </label>
              <div className="form__browsefileBox">
                <div
                  className="form__browsefile form__browsefile--sm"
                  style={{ paddingRight: 0 }}
                >
                  <label className="btn btn__primary" for="UploadFile">
                    Browse File
                  </label>
                  <input
                    type="file"
                    id="UploadFile"
                    disabled
                    className="form__uploadfiled"
                  />
                  <input
                    type="text"
                    disabled
                    className="form__input"
                    placeholder="No file chosen"
                  />
                  {field?.isDefault ? null : (
                    <button
                      className="btn border_0"
                      style={{ justifyContent: "flex-end" }}
                      onClick={() => onRemoveField(index)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>
              <p className="form__infoText">
                <strong>Supported file type:</strong>{" "}
                {Array.isArray(field?.extensionAllowed)
                  ? field?.extensionAllowed?.map((type) => `.${type}`).join(",")
                  : ""}
                &nbsp;
                <span>
                  <strong>Max file size:</strong> {field?.size}MB
                </span>
              </p>
            </div>
            {index == allFieldsData?.length - 1 ? (
              <p className="para para--right">
                <button className="link link__primary" onClick={showAddForm}>
                  +{notDefaultFields?.length == 5 ? "Edit" : "Add More/Edit"}{" "}
                  Fields
                </button>
              </p>
            ) : null}
          </div>
        ) : field?.type == "date" ? (
          <div key={`${index}`} className="formCol">
            <div className="form__Field">
              <label for="input-field" className="form__label">
                {field?.name ?? field?.label}
                {field?.required ? (
                  <span style={{ color: "red" }}> * </span>
                ) : null}
              </label>
              <div className="form__groupField">
                <DateSelector
                  name="callDate"
                  value={formData.callDate}
                  onChange={handleChange}
                  placeholder="DD/MM/YYYY"
                  isGroupField
                  disabled
                  minDate={moment()}
                />
                {field?.isDefault ? null : (
                  <button className="btn" onClick={() => onRemoveField(index)}>
                    <TrashIcon />
                  </button>
                )}
              </div>
            </div>
            {index == allFieldsData?.length - 1 ? (
              <p className="para para--right">
                <button className="link link__primary" onClick={showAddForm}>
                  +{notDefaultFields?.length == 5 ? "Edit" : "Add More/Edit"}{" "}
                  Fields
                </button>
              </p>
            ) : null}
          </div>
        ) : field?.type == "phone" ? (
          <div key={`${index}`} className="formCol">
            <div className="form__Field">
              <label for="input-field" className="form__label">
                {field?.name ?? field?.label}
                {field?.required ? (
                  <span style={{ color: "red" }}> * </span>
                ) : null}
              </label>
              <div className="form__groupField form__groupField--phonenumber">
                <select
                  className="form__select form__select--sm"
                  name="countryCallingCode"
                  value={formData.countryCallingCode}
                  disabled
                >
                  {ALL_CALLING_CODES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  id="phonenumber"
                  disabled
                  name="phoneNumber"
                  className="form__input form__input--sm"
                  placeholder="Phone Number"
                />
                {field?.isDefault ? null : (
                  <button className="btn" onClick={() => onRemoveField(index)}>
                    <TrashIcon />
                  </button>
                )}
              </div>
            </div>
            {index == allFieldsData?.length - 1 ? (
              <p className="para para--right">
                <button className="link link__primary" onClick={showAddForm}>
                  +{notDefaultFields?.length == 5 ? "Edit" : "Add More/Edit"}{" "}
                  Fields
                </button>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={
        isTabSelected
          ? "tabsContent__content tabsContent__content--selected"
          : "tabsContent__content"
      }
    >
      {isShowAddFormModal ? (
        <AddFormFieldsModal
          allFieldsData={notDefaultFields}
          onAddFieldData={onAddFieldData}
          onClose={closeAddForm}
          checkFormFieldById={checkFormFieldById}
        />
      ) : null}
      {addVariableModalVisible?.visible ? (
        <AddVariableModal
          assistantId={assistantId}
          onSubmit={onSubmitAddVariable}
          onClose={() =>
            setAddVariableModalVisible({
              variableFor: null,
              visible: false,
            })
          }
        />
      ) : null}
      <div className="formRow">
        <div className="formCol">
          <div className="form__Field">
            <label for="formActions" className="form__label">
              Form Actions
            </label>
            <MultiSelectDropdown
              id="formActions"
              name="formActions"
              options={ALL_FORM_ACTIONS}
              selectedValue={formActionData}
              onChange={handleFormActionDataData}
            />
          </div>
        </div>
      </div>
      <div className="formRow">
        <div className="formCol">
          <div className="form__Field">
            <label
              htmlFor="instructions"
              className="form__label form__label--flex"
            >
              <span>
                Instructions<span style={{ color: "red" }}> * </span>
              </span>
              <PlusIconBtn
                onClick={() =>
                  setAddVariableModalVisible({
                    variableFor: "instructions",
                    visible: true,
                  })
                }
              />
            </label>
            <ReactQuillEditor
              id="instructions"
              name="instructions"
              theme="snow"
              value={formData?.form?.instructions}
              placeholder="Enter your instructions here..."
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  form: {
                    ...prev?.form,
                    instructions: value,
                  },
                }))
              }
            />
          </div>
        </div>
      </div>

      {isData ? (
        <>
          <div className="formRow formRow__2">
            <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
              <SortableContext items={allFieldsData}>
                {allFieldsData?.map((field, index) => (
                  <FormFieldItem field={field} index={index} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </>
      ) : (
        <div className="CreateformBox" style={{ marginBottom: 10 }}>
          <h3 className="title title--h4">Create your Form</h3>
          <div className="title title--h4">
            <div className="form__checkboxSubField">
              <div className="form__checkbox form__checkbox--gray">
                <div className="form__checkbox__option m_0">
                  <input
                    type="checkbox"
                    id="MarkAsRequired"
                    name="required"
                    checked={formFieldData?.required ?? false}
                    onChange={handleChange}
                    className="form__checkbox__input"
                  />
                  <label for="MarkAsRequired" className="form__checkbox__label">
                    Mark as required
                    <sup className="form__label__asterisk">*</sup>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="formRow">
            <div className="formCol">
              <div className="form__Field">
                <label for="labelName " className="form__label">
                  Label Name
                </label>
                <input
                  type="text"
                  id="labelName"
                  name="label"
                  className="form__input form__input--sm"
                  placeholder="Label Name"
                  value={formFieldData.label}
                  onChange={handleChange}
                />
                {formError?.formLabelError && (
                  <p className="form__error">{formError?.formLabelError}</p>
                )}
              </div>
            </div>
          </div>
          <div className="formRow">
            <div className="formCol">
              <div className="form__Field">
                <label for="fieldType " className="form__label">
                  Field Type
                </label>
                <select
                  className="form__select form__select--sm"
                  id="fieldType"
                  name="type"
                  value={formFieldData.type}
                  onChange={handleChange}
                >
                  <option value="" selected disabled>
                    -Select-
                  </option>
                  <option value="text">Text</option>
                  <option value="date">Date</option>
                  <option value="number">Number</option>
                  <option value="url">Url</option>
                  <option value="upload">Upload</option>
                </select>
              </div>
            </div>
          </div>
          {formFieldData?.type == "upload" ? (
            <>
              <div className="formRow">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="labelName " className="form__label">
                      File Size (MB)
                    </label>
                    <input
                      type="number"
                      id="labelName"
                      name="size"
                      className="form__input form__input--sm"
                      placeholder="File Size"
                      min={1}
                      max={10}
                      value={formFieldData?.size}
                      onChange={handleChange}
                    />
                    {formError?.formSizeError && (
                      <p className="form__error">{formError?.formSizeError}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="formRow">
                <div className="formCol">
                  <div className="form__Field">
                    <label for="extensionAllowed " className="form__label">
                      File Type
                    </label>
                    <MultiSelectDropdown
                      id="extensionAllowed"
                      name="extensionAllowed"
                      options={INITIAL_FILE_TYPES}
                      selectedValue={formFieldData?.extensionAllowed}
                      onChange={(valData) => {
                        const selectedList = Array.isArray(valData)
                          ? valData.map((item) => item?.value)
                          : [];

                        setFormFieldData((prev) => ({
                          ...prev,
                          extensionAllowed: selectedList,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <div className="formRow">
            <div className="formCol formCol--flex">
              <div className="form__switchBtn">
                <div className="form__switchBtn__option">
                  <label for="visibility-1-main" className="form__label">
                    Visibility
                  </label>
                  <div className="switchBtn">
                    <input
                      type="checkbox"
                      className="toggle"
                      id="visibility-1-main"
                      name="visible"
                      checked={formFieldData?.visible ?? false}
                      onChange={handleChange}
                    />
                    <label for="visibility-1-main">
                      <span className="on">ON</span>
                      <span className="off">OFF</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="btn__group">
            <button
              disabled={
                !formFieldData.label ||
                formError?.formLabelError ||
                !formFieldData.type ||
                (formFieldData.type === "upload" &&
                  (!formFieldData?.size || formError?.formSizeError)) ||
                (formFieldData?.type === "upload" &&
                  (!formFieldData?.extensionAllowed ||
                    formFieldData?.extensionAllowed?.length == 0))
              }
              className="btn btn__primary"
              onClick={() => onAddFieldData([formFieldData])}
            >
              Add Field
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormTab;
